// Helper-e pentru pagina "Mobile Hub" (`/mobile-hub`) — stratul curat de acces rapid
// pentru telefon. Reutilizează sursele existente: item-ele de meniu CRM din
// `navigationDrawerMenu.mjs` și rolurile din `roleHelpers.mjs`. Nu duplică logica de
// business; agregă doar ce afișează hub-ul mobil.

import { NCRM_NAVIGATION_ITEMS, filterNavigationItems } from "./navigationDrawerMenu.mjs";
import { normalizeRoleKey } from "./roleHelpers.mjs";

export const MOBILE_HUB_ROUTE = "/mobile-hub";
// Opt-out per-user pentru redirectul automat de pe mobil (userul a ales home clasic).
export const MOBILE_HUB_OPT_OUT_KEY = "mobile-hub-opt-out";

// Rolurile care văd CRM-ul (deci Mobile Hub-ul este util pentru ele). Aliniat cu
// NCRM_BOARD_ROLES din router + super-rolurile.
const MOBILE_HUB_ROLES = Object.freeze([
  "admin",
  "miniadmin",
  "crm-admin",
  "crm-operator",
  "crm-caller",
  "caller",
]);

export const isMobileHubEligible = (roles) => {
  const set = roles instanceof Set ? roles : new Set(Array.isArray(roles) ? roles : []);
  const normalized = new Set();
  set.forEach((role) => {
    const key = normalizeRoleKey(role);
    if (key) normalized.add(key);
  });
  return MOBILE_HUB_ROLES.some((role) => normalized.has(role));
};

// Rolurile care pot CREA înregistrări CRM (contact/lead/deal) — aliniat cu sursele
// autoritative din backend: `CONTACTS_WRITE_ROLES` (contacts/router.js) și `DEAL_ROLES`
// (deals/model.js). `crm-admin` intră prin alias-ul `crm-operator` (roleHelpers).
// Rolurile de tip caller (`crm-caller`/`caller`) și `crm-default` au doar acces de
// lookup/read, deci NU trebuie să vadă CTA-urile de creare din hub (altfel acțiunea
// se deschide și eșuează cu 403). Activitățile rămân separate: callerii au voie să
// creeze activități (`ACTIVITY_ROLES` îi include), deci acel CTA nu e gate-uit aici.
const CRM_RECORD_WRITE_ROLES = Object.freeze(["crm-operator", "admin", "miniadmin"]);

export const canCreateCrmRecords = (normalizedRoles) => {
  const set =
    normalizedRoles instanceof Set
      ? normalizedRoles
      : new Set(Array.isArray(normalizedRoles) ? normalizedRoles : []);
  return CRM_RECORD_WRITE_ROLES.some((role) => set.has(role));
};

// Subtitlu + tip badge per rută CRM, pentru cardul de meniu. Cheile i18n stau sub
// `mobileHub.menu.*`. Rutele necuprinse cad pe subtitlu gol (fără badge).
const MOBILE_MENU_META = Object.freeze({
  "/ncrm": { subtitleKey: "mobileHub.menu.crmBoard" },
  "/ncrm/activities": { subtitleKey: "mobileHub.menu.activities", badge: "activities" },
  "/ncrm/contacts": { subtitleKey: "mobileHub.menu.contacts" },
  "/ncrm/leads": { subtitleKey: "mobileHub.menu.leads" },
  "/ncrm/deals": { subtitleKey: "mobileHub.menu.deals" },
  "/ncrm/contracts": { subtitleKey: "mobileHub.menu.contracts" },
  "/ncrm/onboarding": { subtitleKey: "mobileHub.menu.onboarding" },
  "/ncrm/onboarding/templates": { subtitleKey: "mobileHub.menu.onboardingTemplates" },
  "/ncrm/products": { subtitleKey: "mobileHub.menu.products" },
  "/ncrm/data-integrations": { subtitleKey: "mobileHub.menu.dataIntegrations" },
  "/ncrm/workflows": { subtitleKey: "mobileHub.menu.workflows" },
});

// Construiește lista de meniu pentru cardul "MENIU CRM", filtrată pe roluri/licență
// exact ca navbar-ul. Sursa item-elor este `NCRM_NAVIGATION_ITEMS`.
export const buildMobileMenuItems = ({
  baseRoles = new Set(),
  normalizedRoles = new Set(),
  licenseSnapshot = null,
} = {}) =>
  filterNavigationItems(NCRM_NAVIGATION_ITEMS, {
    baseRoles,
    normalizedRoles,
    licenseSnapshot,
  }).map((item) => {
    const meta = MOBILE_MENU_META[item.route] || {};
    return {
      titleKey: item.titleKey,
      subtitleKey: meta.subtitleKey || null,
      badge: meta.badge || null,
      icon: item.icon,
      route: item.route,
    };
  });

const ACTIVITY_TYPE_ICONS = Object.freeze({
  call: "mdi-phone-outline",
  meeting: "mdi-account-group-outline",
  email: "mdi-email-outline",
  todo: "mdi-checkbox-marked-circle-outline",
  task: "mdi-checkbox-marked-circle-outline",
  note: "mdi-note-text-outline",
  reminder: "mdi-bell-outline",
});

export const activityTypeIcon = (type) =>
  ACTIVITY_TYPE_ICONS[String(type || "").trim().toLowerCase()] || "mdi-calendar-check-outline";

const pad2 = (value) => String(value).padStart(2, "0");

// Extrage "HH:mm" din `due_at` (acceptă "YYYY-MM-DD HH:mm:ss" sau ISO). Fără dependențe
// de locale ca să evităm variații de format (regula din docs/design.md).
export const formatActivityClock = (dueAt) => {
  const raw = String(dueAt || "").trim();
  if (!raw) return "";
  const match = raw.match(/[T\s](\d{2}):(\d{2})/);
  if (match) return `${match[1]}:${match[2]}`;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  return `${pad2(parsed.getHours())}:${pad2(parsed.getMinutes())}`;
};

export const normalizeActivityItem = (row = {}) => {
  const id = Number(row?.id);
  if (!Number.isFinite(id) || id <= 0) return null;
  const type = String(row?.activity_type || row?.activityType || "").trim().toLowerCase();
  const title = String(row?.title || "").trim();
  return {
    id,
    type,
    icon: activityTypeIcon(type),
    time: formatActivityClock(row?.due_at ?? row?.dueAt),
    title,
    dueAt: row?.due_at ?? row?.dueAt ?? null,
    entityType: String(row?.entity_type || row?.entityType || "").trim().toLowerCase(),
    entityId: row?.entity_id ?? row?.entityId ?? null,
  };
};

// Normalizează payload-ul de la `GET /ncrm/activity/widgets/summary` la structura de care
// are nevoie cardul de activități din hub.
export const normalizeActivitySummary = (data = {}) => {
  const today = data?.today || {};
  const overdue = data?.overdue || {};
  const upcoming = data?.upcoming || {};
  const items = (Array.isArray(today.items) ? today.items : [])
    .map((row) => normalizeActivityItem(row))
    .filter(Boolean);
  return {
    todayCount: Number(today.count || 0),
    overdueCount: Number(overdue.count || 0),
    upcomingCount: Number(upcoming.count || 0),
    todayItems: items,
  };
};
